import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserIcon, AlertTriangleIcon, ArrowRightIcon, CheckCircleIcon, MoreVerticalIcon, EyeIcon, XIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { getActiveAlerts, Alert, dismissAlert } from '../../services/alertService';
import { toast } from 'react-hot-toast';
export const StudentsNeedingAttentionSection = () => {
  const {
    currentUser
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // State for 3-dot menu
  const [openMenuAlertId, setOpenMenuAlertId] = useState<string | null>(null);
  useEffect(() => {
    const fetchActiveAlerts = async () => {
      if (!currentUser?.uid) return;
      try {
        setLoading(true);
        setError(null);
        // Fetch ONLY active alerts from Reports system - single source of truth
        const activeAlerts = await getActiveAlerts(currentUser.uid);
        // Take top 5 most recent alerts
        setAlerts(activeAlerts.slice(0, 5));
      } catch (err) {
        console.error('Error fetching active alerts:', err);
        setError('Failed to load alerts');
      } finally {
        setLoading(false);
      }
    };
    fetchActiveAlerts();
    // Listen for alert updates from Reports page
    const handleAlertUpdate = () => fetchActiveAlerts();
    window.addEventListener('alertCreated', handleAlertUpdate);
    window.addEventListener('alertDismissed', handleAlertUpdate);
    window.addEventListener('alertDeleted', handleAlertUpdate);
    window.addEventListener('alertUpdated', handleAlertUpdate);
    return () => {
      window.removeEventListener('alertCreated', handleAlertUpdate);
      window.removeEventListener('alertDismissed', handleAlertUpdate);
      window.removeEventListener('alertDeleted', handleAlertUpdate);
      window.removeEventListener('alertUpdated', handleAlertUpdate);
    };
  }, [currentUser]);
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuAlertId) {
        setOpenMenuAlertId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuAlertId]);
  // Menu handlers
  const toggleAlertMenu = (alertId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenuAlertId(openMenuAlertId === alertId ? null : alertId);
  };
  const handleViewStudent = (alert: Alert, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenuAlertId(null);
    navigate(`/reports?student=${alert.studentId}`);
  };
  const handleDismissAlert = async (alert: Alert, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenuAlertId(null);
    if (!currentUser?.uid || !alert.id) {
      toast.error('Cannot dismiss alert: missing information');
      return;
    }
    try {
      await dismissAlert(currentUser.uid, alert.id);
      // Update local state
      setAlerts(alerts.filter(a => a.id !== alert.id));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('alertDismissed'));
      toast.success('Alert dismissed successfully');
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };
  if (!currentUser) return null;
  // Loading State
  if (loading) {
    return <div className={`mb-6 rounded-xl shadow-sm p-6 ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1167">
        <div className="flex items-center justify-between mb-4" data-id="element-1168">
          <div className={`h-6 w-48 rounded animate-pulse ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-1169"></div>
        </div>
        <div className="space-y-3" data-id="element-1170">
          {[1, 2, 3].map(i => <div key={i} className={`h-20 rounded-lg animate-pulse ${isDark ? 'bg-navy-700' : 'bg-gray-50'}`} data-id="element-1171"></div>)}
        </div>
      </div>;
  }
  // Empty State
  if (alerts.length === 0 && !error) {
    return <div className={`mb-6 rounded-xl shadow-sm p-6 ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1172">
        <div className="flex items-center justify-between mb-4" data-id="element-1173">
          <h2 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1174">
            <UserIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-1175" />
            Students Needing Attention
          </h2>
          <Link to="/reports" className="text-sm text-[#10B981] hover:text-[#059669] font-medium flex items-center transition-colors" data-id="element-1176">
            View All Reports
            <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1177" />
          </Link>
        </div>
        <div className={`p-4 rounded-lg border ${isDark ? 'bg-navy-700/50 border-navy-600' : 'bg-white border-gray-100'}`} data-id="element-1178">
          <div className="flex items-start gap-3" data-id="element-1179">
            <CheckCircleIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-navy-500' : 'text-gray-400'}`} data-id="element-1180" />
            <div data-id="element-1181">
              <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1182">
                No students need attention right now.
              </p>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1183">
                All students are on track!
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className={`mb-6 rounded-xl shadow-sm ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1184">
      <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'} flex items-center justify-between`} data-id="element-1185">
        <h2 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1186">
          <UserIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-1187" />
          Students Needing Attention
        </h2>
        <Link to="/reports" className="text-sm text-[#10B981] hover:text-[#059669] font-medium flex items-center transition-colors" data-id="element-1188">
          View All Reports
          <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1189" />
        </Link>
      </div>

      <div className="p-4" data-id="element-1190">
        {error ? <div className={`p-4 rounded-lg flex items-center ${isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-600'}`} data-id="element-1191">
            <AlertTriangleIcon className="h-5 w-5 mr-3 flex-shrink-0" data-id="element-1192" />
            <p className="text-sm" data-id="element-1193">{error}</p>
          </div> : <div className="space-y-3" data-id="element-1194">
            {alerts.map(alert => {
          // Truncate reason for preview
          const reasonPreview = alert.reason.length > 60 ? alert.reason.substring(0, 60) + '...' : alert.reason;
          return <div key={alert.id} className={`p-4 rounded-lg border transition-colors duration-150 ${isDark ? 'bg-[#152040] border-[#253660] hover:bg-[#1E3058] hover:border-[#3A5585]' : 'bg-white border-gray-100 hover:bg-gray-100 hover:border-gray-200'}`} data-id="element-1195">
                  <div className="flex items-start justify-between gap-4" data-id="element-1196">
                    <div className="flex-1 min-w-0" data-id="element-1197">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1198">
                        <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1199">
                          {alert.studentName}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-red-900/30 text-red-300 border border-red-500/30' : 'bg-red-100 text-red-800 border border-red-200'}`} data-id="element-1200">
                          <AlertTriangleIcon className="h-3 w-3 mr-1" data-id="element-1201" />
                          Alert
                        </span>
                      </div>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1202">
                        {reasonPreview}
                      </p>
                      {alert.practiceHours !== undefined && <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1203">
                          Practice: {alert.practiceHours} hrs/week
                        </p>}
                    </div>

                    {/* 3-dot menu */}
                    <div className="relative flex-shrink-0" data-id="element-1204">
                      <button onClick={e => toggleAlertMenu(alert.id!, e)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-600 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`} aria-label="Student actions" data-id="element-1205">
                        <MoreVerticalIcon className="h-5 w-5" data-id="element-1206" />
                      </button>

                      {/* Dropdown menu */}
                      {openMenuAlertId === alert.id && <div className={`absolute right-0 bottom-full mb-1 w-56 rounded-lg shadow-lg z-50 ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-1207">
                          <button onClick={e => handleViewStudent(alert, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-1208">
                            <EyeIcon className="h-4 w-4 mr-3" data-id="element-1209" />
                            View Student
                          </button>
                          <button onClick={e => handleDismissAlert(alert, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-orange-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-1210">
                            <XIcon className="h-4 w-4 mr-3" data-id="element-1211" />
                            Dismiss
                          </button>
                        </div>}
                    </div>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </div>;
};